import { useQuery } from "@apollo/client";
import { logError } from "components/providers/logError";
import {
    Division,
    Event,
    EventDivision,
    FormatDefinition,
    Organisation,
} from "graphql/generated/types";
import { useEffect } from "react";
import { GET_EVENTS } from "./GET_EVENTS.gql";
import { EventsQuery } from "./GET_EVENTS.gql.types";
import { storeItem, Timestamped, useRecentStack } from "./useRecentStack";

const LOCAL_STORAGE_KEY = "recentlyViewed";

export interface RecentlyViewedEventData
    extends Pick<Event, "id" | "name" | "status" | "date" | "daysWindow"> {
    organisation: Pick<Organisation, "name" | "logo" | "sportType">;
}

export interface RecentlyViewedEventDivisionData
    extends Pick<EventDivision, "id" | "eventDivisionId" | "entryCount"> {
    division: Pick<Division, "name">;
    event: Pick<Event, "id" | "name">;
    formatDefinition: Pick<FormatDefinition, "isTeams">;
}

export interface RecentlyViewedEvent {
    type: "event";
    data: RecentlyViewedEventData;
}

export interface RecentlyViewedRankingsData {
    seriesId: string;
    seriesName: string;
    divisionId: string;
    divisionName: string;
    organisation: Pick<
        Organisation,
        "name" | "logo" | "sportType" | "shortName"
    >;
}

export interface RecentlyViewedEventDivision {
    type: "eventDivision";
    data: RecentlyViewedEventDivisionData;
}

export interface RecentlyViewedRankings {
    type: "rankings";
    data: RecentlyViewedRankingsData;
}

export type RecentlyViewedItem =
    | RecentlyViewedEvent
    | RecentlyViewedEventDivision
    | RecentlyViewedRankings;

interface UseRecentlyViewedResult {
    items: RecentlyViewedItem[];
}

export const useRecentlyViewed = (): UseRecentlyViewedResult => {
    const [items, setItems] =
        useRecentStack<RecentlyViewedItem>(LOCAL_STORAGE_KEY);

    const eventIds = items
        .filter((item) => item.type === "event")
        .map((item) => item.data.id);

    const { loading, data } = useQuery<EventsQuery>(GET_EVENTS, {
        variables: { ids: eventIds },
        skip: eventIds.length === 0,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });

    useEffect(() => {
        if (loading || !data) return;

        const updatedItems = items.map((item) => {
            if (item.type !== "event") {
                return item;
            }

            const freshItem = data.events.find(
                (event: RecentlyViewedEventData) => event.id === item.data.id,
            );
            if (!freshItem) {
                return item;
            }

            return {
                type: "event",
                lastViewed: item.lastViewed,
                data: { ...freshItem },
            };
        });

        setItems(updatedItems as Timestamped<RecentlyViewedEvent>[]);
    }, [loading, data]);

    return { items };
};

export const addRecentlyViewed = (item: RecentlyViewedItem) => {
    storeItem(LOCAL_STORAGE_KEY, item, itemKey);
};

const itemKey = (item: RecentlyViewedItem) => {
    const { type, data } = item;
    if (type === "event") return `event-${data.id}`;
    if (type === "eventDivision") return `eventDivision-${data.id}`;
    if (type === "rankings")
        return `rankings-${data.seriesId}-${data.divisionId}`;

    logError(new Error(`Unknown RecentlyViewedItem type: ${type}`));
    return null;
};
